import { formatPercent, parseMoneyToCents, tt } from '@agos/shared'

import { percentOf } from '@agos/customer-core'

/**
 * A coupon, valued the way the order will actually be charged.
 *
 * `POST /api/v1/coupons/validate` is public, read-only, and answers the coupon's `type`, `value`,
 * `productId` and a `label`. It also answers a `discountAmount`, and **this module ignores it** —
 * FB-C07. The validator finishes on the five-cent step:
 *
 * ```java
 * return roundToNearestFiveCents(discount);
 * ```
 *
 * while `WebOrderDiscounts`, which decides the charge, finishes to the cent. On the restored data the
 * two disagree for **411 of the 494** orders ever placed with a percentage coupon. So the figure the
 * endpoint exists to supply is the one number in its response not to use.
 *
 * The rejection messages *are* used word for word: they are written for the customer and the controller
 * says so — *"its rejection messages are shown to the customer word for word."* Inventing our own would
 * mean two vocabularies for the same refusal.
 *
 * **Product-scoped coupons pin to their lines** (FB-C08). The validator asks the client to do this —
 * *"the client knows the item's price and computes the discount itself"* — and today's order service
 * does not, because `readCoupon` never selects `product_id`. Zero such coupons exist, so nothing reaches
 * it; the rule implemented here is the one a product coupon means.
 */

export interface AppliedCoupon {
  couponId: number
  code: string
  type: string
  /** Percent, or euros for a fixed coupon — as sent, in the coupon's own unit. */
  value: number
  /** Set when the coupon is scoped to one product rather than the basket. */
  productId: number | null
  /** The server's own wording for what this coupon is: `20%`, `5,00 €`. */
  label: string
  /**
   * §9.782, BACKEND.md §10A.436 - the organization this code supports and its share of the food;
   * null for every other coupon.
   */
  organization: { name: string; percent: number } | null
}

export interface CouponLine {
  productId: number
  quantity: number
  unitCents: number
}

/** The applied coupon, or `null` when the response is a refusal rather than a valuation. */
export function couponFrom(payload: unknown): AppliedCoupon | null {
  if (typeof payload !== 'object' || payload === null) return null
  const row = payload as Record<string, unknown>
  if (row.valid !== true || typeof row.code !== 'string') return null
  return {
    couponId: typeof row.couponId === 'number' ? row.couponId : 0,
    code: row.code,
    type: typeof row.type === 'string' ? row.type : '',
    value: typeof row.value === 'number' ? row.value : 0,
    productId: typeof row.productId === 'number' ? row.productId : null,
    label: typeof row.label === 'string' ? row.label : '',
    organization: organizationOf(row.organization),
  }
}

/** The organization a validated code supports, or null - a name and a share above nought, or nothing. */
function organizationOf(value: unknown): AppliedCoupon['organization'] {
  if (typeof value !== 'object' || value === null) return null
  const row = value as Record<string, unknown>
  const percent = typeof row.percent === 'number' ? row.percent : Number(row.percent)
  if (typeof row.name !== 'string' || row.name.trim() === '' || !(percent > 0)) return null
  return { name: row.name.trim(), percent }
}

/**
 * What the customer is told once an organization's code is applied - §9.782. They typed the code to
 * support somebody, and the line says it reached them; `''` for every other coupon.
 */
export function organizationNote(coupon: Pick<AppliedCoupon, 'organization'>): string {
  if (coupon.organization === null) return ''
  return `${tt('Z tejto objednávky pošleme')} ${formatPercent(coupon.organization.percent)} ${tt('z jedla organizácii')} ${coupon.organization.name}.`
}

/** The server's refusal, verbatim, or `null` when there was not one. */
export function couponRefusal(payload: unknown): string | null {
  if (typeof payload !== 'object' || payload === null) return null
  const message = (payload as Record<string, unknown>).error
  return typeof message === 'string' && message.trim() !== '' ? message : null
}

/**
 * What the coupon takes off, in cents.
 *
 * Percentages are `Money.cents` — `WebOrderDiscounts.percentOf` exactly. Fixed coupons are capped at the
 * food, which is the server's `coupon.value().min(subtotal)` and the reason a 10 € voucher on a 6 €
 * order is worth 6 €.
 */
export function couponCents(
  coupon: AppliedCoupon | null,
  lines: readonly CouponLine[],
  subtotalCents: number,
): number {
  if (coupon === null) return 0

  if (coupon.productId !== null) {
    // FB-C08: the coupon's own lines, not the basket.
    const scoped = lines.filter((line) => line.productId === coupon.productId)
    const lineCents = scoped.reduce((sum, line) => sum + line.unitCents * line.quantity, 0)
    if (lineCents <= 0) return 0
    return coupon.type === 'percent'
      ? percentOf(lineCents, coupon.value)
      : Math.min(lineCents, parseMoneyToCents(coupon.value))
  }

  if (coupon.type === 'percent') return percentOf(subtotalCents, coupon.value)
  if (coupon.type === 'fixed') return Math.min(subtotalCents, parseMoneyToCents(coupon.value))
  // A type this build does not know. Zero, and the coupon still shows as applied with the server's own
  // label — the alternative is guessing a number the charge will contradict.
  return 0
}

/**
 * Whether the basket still holds anything the coupon applies to.
 *
 * A product coupon survives in state while its item is removed, and a discount line for a product that
 * is no longer in the basket reads as a bug in the site.
 */
export function stillApplies(coupon: AppliedCoupon | null, lines: readonly CouponLine[]): boolean {
  if (coupon === null) return false
  if (coupon.productId === null) return true
  return lines.some((line) => line.productId === coupon.productId && line.quantity > 0)
}

/**
 * What a referral code is worth, as the server said it.
 *
 * `POST /orders/validate-referral` answers a `discountType` and a `discountValue`, and the preview shows
 * that rather than a figure of its own — a referral the customer typed correctly and a preview that
 * disagreed with the receipt is FB-C07 in a different coat.
 *
 * The percentage is taken with `percentOf`, which is `Money.cents(amount × percent / 100)` — the
 * server's own arithmetic, to the cent and never to the coin.
 */
export function referralDiscountCents(answer: unknown, subtotalCents: number): number {
  if (typeof answer !== 'object' || answer === null) return 0
  const row = answer as { discountType?: unknown; discountValue?: unknown }
  const value =
    typeof row.discountValue === 'number'
      ? row.discountValue
      : typeof row.discountValue === 'string'
        ? Number(row.discountValue)
        : Number.NaN
  if (!Number.isFinite(value) || value <= 0) return 0
  if (row.discountType === 'percent') return percentOf(subtotalCents, value)
  // Anything else is a fixed amount in euros, which is how the coupon endpoint answers too.
  return Math.round(value * 100)
}
